/**
 * SVD option-row builder — the per-product VDA row loop, extracted from `product-form-legacy.tsx` so more
 * than one surface can render SVD pickers.
 *
 * It owns everything between "a product with `vdas`" and "an ordered list of renderable rows": `.order`
 * sorting, `isHidden` filtering, cascade availability, primary-swatch color grouping, free-swatch button
 * placement, the red-dot hydration seam, and the trailing engraving row. Each row is produced by
 * `renderVdaOption`, which routes DS vs legacy internally — so both design branches are covered by one call.
 *
 * Consumers:
 * - `ProductFormLegacy` (single-product PDP buy box) — the original home; behaviour is unchanged.
 * - Bundle child cards (SVD bundle PDP) — each card renders one constituent's rows. A bundle can't reuse
 *   `ProductFormWrapper` (it is a whole buy box: cart, price, reviews, ATC), but it can reuse this.
 *
 * The hook is ALWAYS called (React rules of hooks); `enabled: false` short-circuits to an empty list, which
 * is what keeps the legacy (non-SVD) flow byte-identical — it renders `productOptions` instead.
 */

import React, { useEffect, useState } from 'react'

import type SvdProduct from '@src/quince-site-common/src/domains/svd/svd-product'
import type { SvdSelection } from '@src/quince-site-common/src/domains/svd/svd-product.types'
import * as svdCascadeHelper from '@src/quince-site-common/src/helpers/svd-cascade-helper'
import { getSvdColorGroups } from '@src/quince-site-common/src/helpers/svd-color-group-helper'

import { renderSvdEngraving, renderVdaOption } from './product-form-shared'

/** One renderable option row, sorted by `displaySequence` alongside legacy/add-on rows by the caller. */
export interface SvdOptionRow {
  node: React.ReactNode
  displaySequence: number
  key: string
}

export interface UseSvdOptionRowsParams {
  /** Gate — `false` returns no rows (legacy flow). The hook still runs, so hook order is stable. */
  enabled: boolean
  /** The SVD product whose `vdas` drive the rows; `undefined` yields no rows. */
  product?: SvdProduct
  /** SVD-native selection for THIS product (per-constituent on a bundle). */
  svdSelected: SvdSelection[]
  onOptionSelect: (vdaKey: string, valueKey: string) => void
  t: (key: string) => string
  /** "Order free swatches" button, placed under the primary swatch section. Omit where it doesn't apply. */
  renderSwatchSelectionButton?: () => React.ReactNode
  /** Engraving is a buy-box concern; bundle cards opt out. Defaults to `true` (product-form parity). */
  includeEngraving?: boolean
  /**
   * On-sale red-dot gate — legacy parity for `isPDP && !isBundle` (`option-selector-ds.tsx`): the cue only
   * renders on a full PDP, never in the cart popup / buy-now / post-order flows and never on a bundle
   * constituent row. Defaults to `false`, which is what every current caller of this hook wants (the
   * bundle card); the buy-box rows come from `useSvdOptionNodes`, which passes its host's `isPDP`.
   */
  showOnSaleRedDot?: boolean
  productFormOptionsClass?: string
  optionSelectorClassName?: string
  legendClassName?: string
  inputOptionError?: string
}

export function useSvdOptionRows({
  enabled,
  product,
  svdSelected,
  onOptionSelect,
  t,
  renderSwatchSelectionButton,
  includeEngraving = true,
  showOnSaleRedDot = false,
  productFormOptionsClass,
  optionSelectorClassName,
  legendClassName,
  inputOptionError,
}: UseSvdOptionRowsParams): SvdOptionRow[] {
  // The "Order free swatches" button sits directly under the PRIMARY swatch (David 2026-07-17). It renders
  // only when swatch data feeds `renderSwatchSelectionButton` (SVD swatch-product data is hydration-tier →
  // hidden until it arrives), but the placement is wired here so no re-plumb is needed when it lands.
  const primarySwatchKey = enabled && product ? svdCascadeHelper.getPrimarySwatchKey(product) : undefined
  // Red-dot hydration seam: SSR + first client render honour the server `showRedDot` (hasMounted=false);
  // after mount the client re-derives it from the current (time-based) promo window. Gated on the primary
  // swatch AND on the host opting into the cue, so neither the legacy flow nor a suppressed surface
  // triggers this extra render.
  const [redDotsMounted, setRedDotsMounted] = useState(false)
  useEffect(() => {
    if (primarySwatchKey && showOnSaleRedDot) setRedDotsMounted(true)
  }, [primarySwatchKey, showOnSaleRedDot])

  const rows: SvdOptionRow[] =
    enabled && product?.vdas
      ? [...product.vdas]
          .sort((a, b) => a.order - b.order)
          .filter(vda => !vda.isHidden)
          .flatMap(vda => {
            const availableValueKeys = svdCascadeHelper.getAvailableValueKeysForVda(product, vda, svdSelected)
            // Primary-swatch color grouping (parity with the legacy Color-option split): render one row per
            // group (core first, then named groups). `undefined` → no grouping → the normal single row.
            const colorGroups = vda.key === primarySwatchKey ? getSvdColorGroups(vda, t('core_color')) : undefined

            if (colorGroups) {
              return colorGroups.map((group, groupIndex) => ({
                node: renderVdaOption({
                  vda,
                  svdSelected,
                  availableValueKeys,
                  onOptionSelect,
                  product,
                  hasMounted: redDotsMounted,
                  showOnSaleRedDot,
                  valueKeyFilter: group.valueKeys,
                  legendLabel: group.displayName,
                  // Free-swatch button sits under the LAST color-group row (i.e. under the whole swatch section).
                  renderAfterOption: groupIndex === colorGroups.length - 1 ? renderSwatchSelectionButton : undefined,
                  productFormOptionsClass,
                  optionSelectorClassName,
                  legendClassName,
                  t,
                }),
                displaySequence: vda.order,
                // eslint-disable-next-line i18next/no-literal-string -- React key fallback, not display copy
                key: `${vda.key}__${group.key || 'core'}`,
              }))
            }

            return [
              {
                node: renderVdaOption({
                  vda,
                  svdSelected,
                  availableValueKeys,
                  onOptionSelect,
                  product,
                  hasMounted: redDotsMounted,
                  showOnSaleRedDot,
                  renderAfterOption: vda.key === primarySwatchKey ? renderSwatchSelectionButton : undefined,
                  productFormOptionsClass,
                  optionSelectorClassName,
                  legendClassName,
                  t,
                }),
                displaySequence: vda.order,
                key: vda.key,
              },
            ]
          })
      : []

  // Engraving is a first-class SVD object (not a VDA) — rendered LAST, after all VDA rows (David 2026-07-17).
  if (enabled && includeEngraving && product?.engravingConfig) {
    rows.push({
      node: renderSvdEngraving({
        engravingConfig: product.engravingConfig,
        onOptionSelect,
        inputOptionError,
        productFormOptionsClass,
        legendClassName,
      }),
      displaySequence: Number.MAX_SAFE_INTEGER,
      // eslint-disable-next-line i18next/no-literal-string -- React key, not display copy
      key: 'svd-engraving',
    })
  }

  return rows
}
